/**
 * Landscape lock for a phone duel.
 *
 * A browser tab must enter the Fullscreen API before Android Chrome
 * accepts `screen.orientation.lock`. An installed PWA is already
 * chromeless, so that call is skipped there. Exiting the duel would
 * otherwise leave fullscreen. The lock is still attempted; if Chrome
 * rejects it, the rotate overlay stays.
 *
 * Xiaomi HyperOS ignores a generic `landscape` lock and follows
 * `landscape-primary`. iOS has no lock API. Desktop is left alone
 * (pointer is not coarse).
 */

import { isInstalledPwa } from './usePwaInstall'

type FsDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

type OrientationLockType = 'landscape' | 'landscape-primary'

type LockableOrientation = {
  type?: string
  lock?: (orientation: OrientationLockType) => Promise<void>
  unlock?: () => void
  addEventListener?: (type: 'change', listener: () => void) => void
  removeEventListener?: (type: 'change', listener: () => void) => void
}

const PHONE_MQ =
  '(pointer: coarse) and (max-width: 700px), (pointer: coarse) and (max-height: 500px)'

let armed = false
let locked = false
let pending = false
let weRequestedFullscreen = false
let pointerBound = false
let screenBound = false
let generation = 0

function isPhone(): boolean {
  return window.matchMedia(PHONE_MQ).matches
}

function duelOrientation(): LockableOrientation | null {
  const orientation = (screen as { orientation?: LockableOrientation }).orientation
  return orientation ?? null
}

function fsElement(): Element | null {
  const doc = document as FsDocument
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

function isLandscapeNow(): boolean {
  const type = duelOrientation()?.type ?? ''
  if (type.startsWith('landscape')) return true
  return window.matchMedia('(orientation: landscape)').matches
}

function unbindPointer(): void {
  if (!pointerBound) return
  window.removeEventListener('pointerdown', onFirstPointer, true)
  pointerBound = false
}

function bindPointer(): void {
  if (pointerBound || locked) return
  pointerBound = true
  window.addEventListener('pointerdown', onFirstPointer, true)
}

function onFirstPointer(): void {
  window.removeEventListener('pointerdown', onFirstPointer, true)
  pointerBound = false
  if (!armed || locked) return
  attempt()
}

function onFullscreenChange(): void {
  if (!armed || pending) return
  if (!isInstalledPwa() && !fsElement()) {
    locked = false
    bindPointer()
    return
  }
  if (locked) return
  attempt()
}

function onOrientationChange(): void {
  if (!armed) return
  if (isLandscapeNow()) {
    if (locked) return
    // Already sideways is not a lock. Keep the tap that asks for fullscreen.
    bindPointer()
    attempt()
    return
  }
  if (!locked || pending) return
  locked = false
  bindPointer()
  attempt()
}

function bindScreen(): void {
  if (screenBound) return
  screenBound = true
  document.addEventListener('fullscreenchange', onFullscreenChange)
  document.addEventListener('webkitfullscreenchange', onFullscreenChange)
  duelOrientation()?.addEventListener?.('change', onOrientationChange)
}

function unbindScreen(): void {
  if (!screenBound) return
  screenBound = false
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
  duelOrientation()?.removeEventListener?.('change', onOrientationChange)
}

async function exitFullscreen(): Promise<void> {
  const doc = document as FsDocument
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen()
      return
    }
    await doc.webkitExitFullscreen?.()
  } catch {
    /* already left */
  }
}

function requestFullscreen(): Promise<void> | null {
  if (fsElement()) return Promise.resolve()
  const el = document.documentElement as FsElement
  try {
    if (el.requestFullscreen) {
      return el.requestFullscreen({ navigationUI: 'hide' })
    }
    const webkit = el.webkitRequestFullscreen?.()
    return webkit ? Promise.resolve(webkit) : null
  } catch {
    return null
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/**
 * The lock promise can resolve before the OS finishes turning.
 * Poll briefly so a slow rotation is not unlocked and cancelled.
 */
async function landscapeSettled(): Promise<boolean> {
  for (let i = 0; i < 8; i++) {
    if (isLandscapeNow()) return true
    await wait(100)
  }
  return isLandscapeNow()
}

async function lockLandscape(): Promise<boolean> {
  const lock = duelOrientation()?.lock
  if (!lock) return false
  const order: OrientationLockType[] = ['landscape-primary', 'landscape']
  for (const orientation of order) {
    try {
      await lock(orientation)
    } catch {
      continue
    }
    if (await landscapeSettled()) return true
    try {
      duelOrientation()?.unlock?.()
    } catch {
      /* still portrait */
    }
  }
  return false
}

function attempt(): void {
  if (!armed || locked || pending || !isPhone()) return
  if (!duelOrientation()?.lock) {
    unbindPointer()
    return
  }

  const ticket = generation
  const finish = () => {
    if (ticket !== generation) return Promise.resolve()
    return lockLandscape().then((held) => {
      if (ticket !== generation) return
      const fullscreenOk = isInstalledPwa() || !!fsElement()
      if (held && fullscreenOk && isLandscapeNow()) {
        locked = true
        unbindPointer()
        return
      }
      locked = false
      bindPointer()
    })
  }

  const installedPwa = isInstalledPwa()
  const alreadyFullscreen = !!fsElement()
  pending = true
  const req = installedPwa
    ? Promise.resolve()
    : (requestFullscreen() ?? Promise.resolve())
  void req
    .then(
      () => {
        if (ticket !== generation) {
          if (!installedPwa && !alreadyFullscreen) return exitFullscreen()
          return
        }
        if (!installedPwa && !alreadyFullscreen && fsElement()) {
          weRequestedFullscreen = true
        }
        return finish()
      },
      () => {
        if (ticket !== generation) return
        return finish()
      },
    )
    .catch(() => undefined)
    .finally(() => {
      if (ticket === generation) pending = false
    })
}

/** Call in the same turn as the tap that opens the duel. */
export function armDuelLandscape(): void {
  if (typeof window === 'undefined' || !isPhone()) return
  if (!duelOrientation()?.lock) return
  armed = true
  bindScreen()
  attempt()
  bindPointer()
}

/** Drop the lock when leaving the duel. Fullscreen exits only if we opened it. */
export function releaseDuelOrientation(): void {
  if (typeof window === 'undefined') return
  generation += 1
  armed = false
  locked = false
  pending = false
  unbindPointer()
  unbindScreen()
  try {
    duelOrientation()?.unlock?.()
  } catch {
    /* not locked, or unsupported */
  }
  if (weRequestedFullscreen) {
    weRequestedFullscreen = false
    void exitFullscreen()
  }
}
